import React, { Component } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress,
    Typography,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    Button,
    Divider
} from '@material-ui/core';
import { ChevronLeft, ChevronRight, HelpOutline, CalendarToday, Today } from '@material-ui/icons';
import moment from 'moment';
import 'moment/locale/es';
import { getAllEmployeesUseCase } from '../services/getAllEmployeesUseCase';
import { getTimeRecordsForRangeUseCase } from '../services/getTimeRecordsForRangeUseCase';
import { getHolidaysUseCase, isHoliday } from '../services/getHolidaysUseCase';
import { getAllEmployeeVacationsUseCase, isEmployeeOnVacation } from '../services/getAllEmployeeVacationsUseCase';
import {
    groupRecordsByShift,
    calculateDailyHours,
    calculateWeeklyTotal,
    determineCellState,
    formatHours,
    getTimeRanges,
    calculateEmployeeVacationDays
} from '../helpers/timeCalculationHelpers';
import DayDetailModal from './DayDetailModal';
import './EmployeeControlTable.css';

moment.locale('es');

class EmployeeControlTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            employees: [],
            timeRecordsMap: {}, // { employeeId: { date: [records] } }
            holidays: [],
            vacations: [],
            yearlyVacations: [],
            loading: true,
            currentWeekStart: moment().startOf('isoWeek'), // Monday
            selectedDay: null,
            selectedEmployee: null,
            detailModalOpen: false,
            tempDate: moment().startOf('isoWeek').format('YYYY-MM-DD')
        };
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = async () => {
        this.setState({ loading: true });
        try {
            // Fetch employees
            const employees = await getAllEmployeesUseCase();

            // Get week range
            const { currentWeekStart } = this.state;
            const currentWeekEnd = currentWeekStart.clone().endOf('week');
            const monthStart = currentWeekStart.clone().startOf('month');
            const monthEnd = currentWeekEnd.clone().endOf('month');

            const startRange = monthStart.isBefore(currentWeekStart) ? monthStart : currentWeekStart;
            const endRange = monthEnd.isAfter(currentWeekEnd) ? monthEnd : currentWeekEnd;

            // Fetch holidays for the week
            const holidays = await getHolidaysUseCase(
                startRange.toDate(),
                endRange.toDate()
            );

            // Fetch employee vacations for the week
            const vacations = await getAllEmployeeVacationsUseCase(
                startRange.toDate(),
                endRange.toDate()
            );

            // Fetch time records for all employees
            const timeRecordsMap = {};
            for (const employee of employees) {
                const records = await getTimeRecordsForRangeUseCase(
                    employee.dni,
                    startRange.toDate(),
                    endRange.toDate()
                );

                // Group records by date
                const recordsByDate = {};
                records.forEach(record => {
                    const dateKey = moment(record.timestamp).format('YYYY-MM-DD');
                    if (!recordsByDate[dateKey]) {
                        recordsByDate[dateKey] = [];
                    }
                    recordsByDate[dateKey].push(record);
                });

                timeRecordsMap[employee.dni] = recordsByDate;
            }

            // Fetch all vacations for the current year
            const yearStart = moment().startOf('year');
            const yearEnd = moment().endOf('year');
            const yearlyVacations = await getAllEmployeeVacationsUseCase(
                yearStart.toDate(),
                yearEnd.toDate()
            );

            this.setState({
                employees,
                timeRecordsMap,
                holidays,
                vacations,
                yearlyVacations,
                loading: false
            });
        } catch (error) {
            console.error('Error loading data:', error);
            this.setState({ loading: false });
        }
    }

    handlePreviousWeek = () => {
        const newDate = this.state.currentWeekStart.clone().subtract(1, 'week');
        this.setState(
            {
                currentWeekStart: newDate,
                tempDate: newDate.format('YYYY-MM-DD')
            },
            () => this.loadData()
        );
    }

    handleNextWeek = () => {
        const newDate = this.state.currentWeekStart.clone().add(1, 'week');
        this.setState(
            {
                currentWeekStart: newDate,
                tempDate: newDate.format('YYYY-MM-DD')
            },
            () => this.loadData()
        );
    }

    handleGoToToday = () => {
        const today = moment().startOf('isoWeek');
        this.setState(
            {
                currentWeekStart: today,
                tempDate: today.format('YYYY-MM-DD')
            },
            () => this.loadData()
        );
    }

    handleDateChange = (event) => {
        this.setState({ tempDate: event.target.value });
    }

    applyDateChange = () => {
        const { tempDate } = this.state;
        if (tempDate) {
            const newDate = moment(tempDate);
            if (newDate.isValid()) {
                this.setState(
                    { currentWeekStart: newDate.startOf('isoWeek') },
                    () => this.loadData()
                );
            }
        }
    }

    handleCellClick = (employee, date) => {
        this.setState({
            selectedEmployee: employee,
            selectedDay: date,
            detailModalOpen: true
        });
    }

    handleCloseDetailModal = () => {
        this.setState({
            detailModalOpen: false,
            selectedEmployee: null,
            selectedDay: null
        });

        console.log('Closing detail modal');
        this.loadData();
    }

    handleDetailUpdate = () => {
        // Reload data after update
        this.loadData();
    }

    getWeekDays = () => {
        const { currentWeekStart } = this.state;
        const days = [];
        for (let i = 0; i < 5; i++) {
            days.push(currentWeekStart.clone().add(i, 'days'));
        }
        return days;
    }

    calculateMonthlyTotal = (employeeId) => {
        const { timeRecordsMap } = this.state;
        const records = timeRecordsMap[employeeId] || {};

        let totalHours = 0;

        const { currentWeekStart } = this.state;
        Object.values(records).forEach(dayRecords => {
            const { total } = calculateDailyHours(dayRecords, currentWeekStart.month());
            totalHours += total;
        });

        return totalHours;
    }

    renderDayCell = (employee, date, shift) => {
        const { timeRecordsMap, holidays, vacations } = this.state;
        const dateKey = date.format('YYYY-MM-DD');
        const dayRecords = timeRecordsMap[employee.dni]?.[dateKey] || [];

        // Group by shift
        const grouped = groupRecordsByShift(dayRecords);
        const shiftRecords = grouped[shift] || [];

        // Calculate hours for this shift
        const { morning, afternoon } = calculateDailyHours(dayRecords);
        const shiftHours = morning + afternoon;

        // Check if holiday
        const isHolidayDay = isHoliday(date.valueOf(), holidays);

        // Check if employee is on vacation
        const isOnVacation = isEmployeeOnVacation(employee.dni, date.valueOf(), vacations);

        // Determine cell state (only for the shift, not the whole day)
        const hasRecords = shiftRecords.length > 0;
        const cellState = determineCellState(shiftHours, isHolidayDay, isOnVacation, hasRecords);

        // Get time ranges for display
        const timeRanges = getTimeRanges(shiftRecords);

        return (
            <TableCell
                key={`${employee.dni}-${dateKey}-${shift}`}
                className="day-cell"
                style={{
                    backgroundColor: cellState.color,
                    color: 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    padding: '8px',
                    minWidth: '80px',
                    borderRight: shift === 'afternoon' ? '2px solid rgba(224, 224, 224, 1)' : 'none'
                }}
                onClick={() => this.handleCellClick(employee, date.toDate())}
            >
                {timeRanges.length > 0 ? (
                    <div className="time-ranges">
                        {timeRanges.map((range, idx) => (
                            <div key={idx} className="time-range">
                                {range}
                            </div>
                        ))}
                    </div>
                ) : (
                    <Typography variant="caption" style={{ color: 'white' }}>
                        {cellState.label}
                    </Typography>
                )}
            </TableCell>
        );
    }

    render() {
        const {
            employees,
            loading,
            currentWeekStart,
            timeRecordsMap,
            detailModalOpen,
            selectedEmployee,
            selectedDay
        } = this.state;

        const weekDays = this.getWeekDays();
        const weekEnd = currentWeekStart.clone().add(4, 'days');

        if (loading) {
            return (
                <div className="table-loading">
                    <CircularProgress />
                    <Typography variant="body1" style={{ marginTop: '20px' }}>
                        Cargando datos...
                    </Typography>
                </div>
            );
        }

        return (
            <div className="employee-control-table">
                <div className="week-navigation">
                    <div className="navigation-controls">
                        <IconButton onClick={this.handlePreviousWeek}>
                            <ChevronLeft />
                        </IconButton>
                        <Typography variant="h6">
                            Semana del {currentWeekStart.format('D MMM')} al {weekEnd.format('D MMM YYYY')}
                        </Typography>
                        <IconButton onClick={this.handleNextWeek}>
                            <ChevronRight />
                        </IconButton>
                    </div>

                    <div className="alternative-navigation">
                        <TextField
                            id="date-selector"
                            label="Semana"
                            type="date"
                            value={this.state.tempDate}
                            onChange={this.handleDateChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            variant="outlined"
                            size="small"
                            margin="dense"
                            className="date-picker-field"
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={this.applyDateChange}
                            style={{ marginLeft: '10px', height: '40px', marginTop: '4px' }}
                        >
                            Ir
                        </Button>
                        <Divider orientation="vertical" />
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={this.handleGoToToday}
                            className="today-button"
                            disabled={currentWeekStart.isSame(moment().startOf('isoWeek'), 'day')}
                            style={{ marginLeft: '10px', height: '40px', marginTop: '4px' }}
                        >
                            Hoy
                        </Button>
                    </div>
                </div>

                <Paper className="table-container">
                    <div className="table-scroll">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell rowSpan={2} className="employee-header">
                                        <strong>Empleado</strong>
                                    </TableCell>
                                    {weekDays.map(day => (
                                        <TableCell
                                            key={day.format('YYYY-MM-DD')}
                                            colSpan={2}
                                            align="center"
                                            className="day-header"
                                            style={{ borderRight: '2px solid rgba(224, 224, 224, 1)' }}
                                        >
                                            <strong>{day.format('ddd D')}</strong>
                                        </TableCell>
                                    ))}
                                    <TableCell rowSpan={2} align="center" className="total-header">
                                        <strong>Total Semana</strong>
                                    </TableCell>
                                    <TableCell rowSpan={2} align="center" className="total-header">
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <strong>Total Mes 2</strong>
                                            <Tooltip title="Las horas totales del mes se calculan en base al mes del lunes de la semana actual">
                                                <HelpOutline style={{ fontSize: '16px', marginLeft: '4px', cursor: 'help', color: 'rgba(0, 0, 0, 0.54)' }} />
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                    <TableCell rowSpan={2} align="center" className="total-header">
                                        <strong>Dias libre (20)</strong>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    {weekDays.map(day => (
                                        <React.Fragment key={`sub-${day.format('YYYY-MM-DD')}`}>
                                            <TableCell align="center" className="shift-header">
                                                <strong>Mañana</strong>
                                            </TableCell>
                                            <TableCell align="center" className="shift-header" style={{ borderRight: '2px solid rgba(224, 224, 224, 1)' }}>
                                                <strong>Tarde</strong>
                                            </TableCell>
                                        </React.Fragment>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {employees.map(employee => {
                                    // Calculate weekly total
                                    const weeklyHours = {};
                                    weekDays.forEach(day => {
                                        const dateKey = day.format('YYYY-MM-DD');
                                        const dayRecords = timeRecordsMap[employee.dni]?.[dateKey] || [];
                                        weeklyHours[dateKey] = calculateDailyHours(dayRecords);
                                    });
                                    const weekTotal = calculateWeeklyTotal(weeklyHours);
                                    const monthTotal = this.calculateMonthlyTotal(employee.dni);

                                    return (
                                        <TableRow key={employee.dni}>
                                            <TableCell className="employee-name">
                                                <Typography variant="body2">
                                                    <strong>{employee.name}</strong>
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {employee.position || employee.dni}
                                                </Typography>
                                            </TableCell>
                                            {weekDays.map(day => (
                                                <React.Fragment key={`${employee.dni}-${day.format('YYYY-MM-DD')}`}>
                                                    {this.renderDayCell(employee, day, 'morning')}
                                                    {this.renderDayCell(employee, day, 'afternoon')}
                                                </React.Fragment>
                                            ))}
                                            <TableCell align="center" className="total-cell">
                                                <strong>{formatHours(weekTotal)}</strong>
                                            </TableCell>
                                            <TableCell align="center" className="total-cell">
                                                <strong>{formatHours(monthTotal)}</strong>
                                            </TableCell>
                                            <TableCell align="center" className="total-cell">
                                                <strong>{20 - calculateEmployeeVacationDays(employee.dni, this.state.yearlyVacations)}</strong>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {employees.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={14} align="center">
                                            <Typography color="textSecondary">
                                                No hay empleados registrados
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </Paper>

                {selectedEmployee && selectedDay && (
                    <DayDetailModal
                        open={detailModalOpen}
                        onClose={this.handleCloseDetailModal}
                        employeeId={selectedEmployee.dni}
                        employeeName={selectedEmployee.name}
                        date={selectedDay}
                        records={
                            timeRecordsMap[selectedEmployee.dni]?.[
                            moment(selectedDay).format('YYYY-MM-DD')
                            ] || []
                        }
                        onUpdate={this.handleDetailUpdate}
                    />
                )}
            </div>
        );
    }
}

export default EmployeeControlTable;
